import { CONTAINER_KIND_LABELS, CONTAINER_KIND_PLURALS } from '@cerebro-diem/core';
import type { ContainerKind, ContainerRow, FilingTarget } from '@cerebro-diem/core';
import { CONTAINER_ICONS } from '../features/containers/labels';
import { LIST_LIMIT, reachedLimit } from '../services/queries';
import { inputClass } from './styles';

const KIND_ORDER: readonly ContainerKind[] = ['project', 'area', 'resource'];

function encode(target: FilingTarget | null): string {
  if (!target) return '';
  return 'newName' in target ? `new:${target.kind}` : `${target.kind}:${target.id}`;
}

/**
 * Choose where an item lives: the active containers of the allowed kinds,
 * grouped Projects / Areas / Resources, and with `allowNew` a "New …" choice
 * per kind that creates the container with the filing. The choice shows by its
 * name alone, with its kind as the icon beside it.
 */
export default function ContainerPicker({
  containers,
  kinds,
  value,
  onChange,
  allowNew = false,
  label,
}: {
  containers: readonly ContainerRow[];
  kinds: readonly ContainerKind[];
  value: FilingTarget | null;
  onChange: (target: FilingTarget | null) => void;
  allowNew?: boolean;
  label: string;
}) {
  const active = containers.filter((c) => !c.archived_at);
  const KindIcon = value ? CONTAINER_ICONS[value.kind] : null;

  const choose = (encoded: string) => {
    if (!encoded) return onChange(null);
    const [head, rest] = [encoded.slice(0, encoded.indexOf(':')), encoded.slice(encoded.indexOf(':') + 1)];
    const newKind = head === 'new' ? KIND_ORDER.find((kind) => kind === rest) : undefined;
    if (newKind) return onChange({ kind: newKind, newName: '' });
    const chosen = active.find((c) => c.kind === head && c.id === rest);
    onChange(chosen ? { kind: chosen.kind, id: chosen.id } : null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {value && KindIcon && (
          <KindIcon
            role="img"
            aria-label={CONTAINER_KIND_LABELS[value.kind]}
            className="w-5 h-5 shrink-0 text-on-surface-variant"
          />
        )}
        <select className={inputClass} value={encode(value)} onChange={(e) => choose(e.target.value)} aria-label={label}>
          <option value="">Choose…</option>
          {KIND_ORDER.filter((kind) => kinds.includes(kind)).map((kind) => (
            <optgroup key={kind} label={CONTAINER_KIND_PLURALS[kind]}>
              {active
                .filter((c) => c.kind === kind)
                .map((c) => (
                  <option key={c.id} value={`${kind}:${c.id}`}>
                    {c.name}
                  </option>
                ))}
              {allowNew && <option value={`new:${kind}`}>New {CONTAINER_KIND_LABELS[kind].toLowerCase()}…</option>}
            </optgroup>
          ))}
        </select>
      </div>
      {reachedLimit(containers) && (
        <p className="text-xs text-tertiary">Only your first {LIST_LIMIT.toLocaleString()} containers are listed.</p>
      )}
      {value && 'newName' in value && (
        <input
          className={inputClass}
          value={value.newName}
          onChange={(e) => onChange({ kind: value.kind, newName: e.target.value })}
          placeholder={`Name of the new ${CONTAINER_KIND_LABELS[value.kind].toLowerCase()}`}
          aria-label={`Name of the new ${CONTAINER_KIND_LABELS[value.kind].toLowerCase()}`}
        />
      )}
    </div>
  );
}
